/**
 * @file Leads upload page.
 * @description CSV preview, multipart `leads-upload/upload-async` first, then `leads-upload/latest-job` polling only after upload finishes.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { LeadsService, UploadResult, UploadJob, LatestUploadJobPayload } from '../../services/leadsService';
import { AdvertisersService } from '../../services/advertisersService';
import { CampaignsService } from '../../services/campaignsService';
import { ApiService } from '../../services/apiService';
import { DropdownItem } from '../../types/api/serviceApi';
import { Advertiser, Campaign } from '../../types';
import { PageHeader } from '../../components/ui';
import { cn } from '../../utils/cn';

type UploadType = 'raw' | 'clicker';

interface CsvRow {
  [key: string]: string | number;
}

function safeInt(v: unknown, fallback = 0): number {
  const n = Number.parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Progress for UI: prefer API `progress` (e.g. "45.50"), else derive from processed/total records.
 */
function computeJobPercent(job: UploadJob): number {
  const fromApi = Number.parseFloat(String(job.progress ?? ''));
  if (Number.isFinite(fromApi) && fromApi >= 0) {
    return Math.min(100, Math.max(0, Math.round(fromApi)));
  }
  const total = safeInt(job.total_records, 0);
  const processed = safeInt(job.processed_records, 0);
  if (total > 0) {
    return Math.min(100, Math.max(0, Math.round((processed / total) * 100)));
  }
  return 0;
}

/** Human-readable progress (matches API `progress` string when present). */
function formatProgressFromJob(job: UploadJob): string {
  const raw = String(job.progress ?? '').trim();
  if (raw !== '' && Number.isFinite(Number.parseFloat(raw))) {
    return Number.parseFloat(raw).toFixed(2);
  }
  const total = safeInt(job.total_records, 0);
  const processed = safeInt(job.processed_records, 0);
  if (total > 0) {
    return ((processed / total) * 100).toFixed(2);
  }
  return '0.00';
}

function jobToUploadResult(job: UploadJob): UploadResult {
  const total = safeInt(job.total_records);
  const valid = safeInt(job.inserted_records);
  const duplicates = safeInt(job.duplicate_records);
  const errors = job.error_message ? [String(job.error_message)] : [];
  return {
    total,
    valid,
    duplicates,
    errors,
    duplicate_records_count: duplicates,
    valid_records_count: valid,
  };
}

interface JobSnapshot {
  jobId: string;
  status: string;
  progressDisplay: string;
  processed: number;
  total: number;
  filename?: string;
  inserted: number;
  duplicates: number;
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** How often to call latest-job while waiting for a new job id. */
const POLL_LATEST_MS = 2_000;
/** How often to call status/{id} while the job is running. */
const POLL_STATUS_MS = 2_500;
const JOB_LOOKUP_MAX_MS = 120_000;
const JOB_RUN_MAX_MS = 3_600_000;

const ACTIVE_JOB_STATUSES = ['pending', 'processing', 'queued', 'running', 'in_progress'];
const FAILED_JOB_STATUSES = ['failed', 'error', 'cancelled'];

function isActiveJobStatus(status: string): boolean {
  return ACTIVE_JOB_STATUSES.includes((status || '').toLowerCase());
}

function isFailedJobStatus(status: string): boolean {
  return FAILED_JOB_STATUSES.includes((status || '').toLowerCase());
}

function isJobComplete(job: UploadJob, trustJob: boolean): boolean {
  const st = (job.status || '').toLowerCase();
  const totalR = safeInt(job.total_records, 0);
  const procR = safeInt(job.processed_records, 0);
  const pct = computeJobPercent(job);
  const fullyProcessed =
    trustJob && totalR > 0 && procR >= totalR && pct >= 100;
  return trustJob && (st === 'completed' || fullyProcessed);
}

const LeadsUpload: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  
  const [progress, setProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<'idle' | 'uploading' | 'processing'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [jobSnapshot, setJobSnapshot] = useState<JobSnapshot | null>(null);
  const [lastPolledAt, setLastPolledAt] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<CsvRow[]>([]);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [sources, setSources] = useState<DropdownItem[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // For CSV download: store arrays for each card (replace with real data after upload)
  const [totalRecordsData, setTotalRecordsData] = useState<unknown[]>([]);
  const [validRecordsData, setValidRecordsData] = useState<unknown[]>([]);
  const [duplicateErrorData, setDuplicateErrorData] = useState<unknown[]>([]);

  // Store CSV headers for Data Type dropdown logic
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [uploadType, setUploadType] = useState<UploadType>('raw');

  const pollSessionRef = useRef(0);
  const pollTimerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectFieldClass =
    'w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500';

  const clearJobPollTimer = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearJobPollTimer();
      pollSessionRef.current += 1;
    };
  }, [clearJobPollTimer]);
  // Utility: Convert array of objects to CSV string
  const arrayToCSV = useCallback((arr: unknown[]) => {
    if (!arr.length) return '';
    const firstRow = arr[0] as Record<string, unknown>;
    const headers = Object.keys(firstRow);
    const csvRows = [headers.join(',')];
    for (const row of arr) {
      const rowObj = row as Record<string, unknown>;
      csvRows.push(headers.map(h => `"${(rowObj[h] ?? '').toString().replace(/"/g, '""')}"`).join(','));
    }
    return csvRows.join('\n');
  }, []);

  // Utility: Download CSV file
  const downloadCSV = useCallback((data: unknown[], filename: string) => {
    const csv = arrayToCSV(data);
    if (!csv) {
      setStatusMessage('No data available for download.');
      return;
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [arrayToCSV]);

  // Handlers for card clicks
  const handleTotalRecordsClick = () => {
    if (totalRecordsData.length) {
      downloadCSV(totalRecordsData, 'total_records.csv');
    } else if (uploadResult?.totalRecordsData?.length) {
      downloadCSV(uploadResult.totalRecordsData, 'total_records.csv');
    } else {
      setStatusMessage('No total records data available for download.');
    }
  };
  
  const handleValidRecordsClick = () => {
    if (validRecordsData.length) {
      downloadCSV(validRecordsData, 'valid_records.csv');
    } else if (uploadResult?.validRecordsData?.length) {
      downloadCSV(uploadResult.validRecordsData, 'valid_records.csv');
    } else {
      setStatusMessage('No valid records data available for download.');
    }
  };
  
  const handleDuplicateErrorClick = () => {
    if (duplicateErrorData.length) {
      downloadCSV(duplicateErrorData, 'duplicates_errors.csv');
    } else if (uploadResult?.duplicateErrorData?.length) {
      downloadCSV(uploadResult.duplicateErrorData, 'duplicates_errors.csv');
    } else {
      setStatusMessage('No duplicates/errors data available for download.');
    }
  };

  // Fetch advertisers on load
  useEffect(() => {
    let mounted = true;
    const fetchAdvertisers = async () => {
      try {
        setDataLoading(true);
        const [advertisersList, sourcesList] = await Promise.all([
          AdvertisersService.getAdvertisers(),
          ApiService.getSources(),
        ]);

        if (!mounted) return;
        setAdvertisers(Array.isArray(advertisersList) ? advertisersList : []);
        setSources(Array.isArray(sourcesList) ? sourcesList : []);
      } catch {
        if (!mounted) return;
        setAdvertisers([]);
        setSources([]);
      } finally {
        if (mounted) {
          setDataLoading(false);
        }
      }
    };
    void fetchAdvertisers();
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch campaigns when advertiser is selected (GET /campaigns/advertiser/{id})
  useEffect(() => {
    let mounted = true;

    const fetchCampaigns = async () => {
      if (!selectedAdvertiser) {
        setCampaigns([]);
        setSelectedCampaign('');
        return;
      }

      setCampaignsLoading(true);
      try {
        const list = await CampaignsService.getCampaignsByAdvertiser(selectedAdvertiser);
        if (!mounted) return;
        setCampaigns(Array.isArray(list) ? list : []);
        setSelectedCampaign('');
      } catch {
        if (!mounted) return;
        setCampaigns([]);
      } finally {
        if (mounted) {
          setCampaignsLoading(false);
        }
      }
    };

    void fetchCampaigns();
    return () => {
      mounted = false;
    };
  }, [selectedAdvertiser]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const clearFileAndUploadState = useCallback(() => {
    pollSessionRef.current += 1;
    clearJobPollTimer();
    setUploadResult(null);
    setProgress(0);
    setUploadPhase('idle');
    setStatusMessage('');
    setUploading(false);
    setJobSnapshot(null);
    setLastPolledAt(null);
    setPreviewData([]);
    setCsvHeaders([]);
  }, [clearJobPollTimer]);

  const handleFile = (file: File) => {
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
      setStatusMessage(
        'Excel files are not supported. Please upload a .csv file (use "Download Clicker Demo CSV" or "Download Raw Data Demo CSV").',
      );
      return;
    }

    const validation = LeadsService.validateCsvFile(file);
    if (!validation.valid) {
      setStatusMessage(validation.error || 'Please upload a valid CSV file.');
      return;
    }

    clearFileAndUploadState();
    setSelectedFile(file);
    setStatusMessage('');
    parseForPreview(file);
  };

  const parseForPreview = (file: File) => {
    LeadsService.parseCsvForPreview(
      file,
      (data: unknown[], headers: string[]) => {
        setPreviewData(data as CsvRow[]);
        setCsvHeaders(headers);
        setStatusMessage('');
      },
      (error: Error) => {
        setStatusMessage(`Error parsing CSV: ${error.message}`);
      },
    );
  };

  const handleUploadTypeChange = (nextType: UploadType) => {
    if (nextType === uploadType) return;
    setUploadType(nextType);
    if (nextType === 'raw') {
      setSelectedAdvertiser('');
      setSelectedCampaign('');
      setCampaigns([]);
    } else {
      setSelectedSource('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setStatusMessage('Please select a file to upload.');
      return;
    }

    setUploading(true);
    const session = ++pollSessionRef.current;
    setUploadPhase('uploading');
    setUploadResult(null);
    setJobSnapshot(null);
    setLastPolledAt(null);
    setProgress(0);
    setStatusMessage('Preparing upload...');

    try {
      const resolvedDataType = uploadType === 'clicker' ? 'clicker' : 'lead';
      const selectedSourceRow = sources.find(
        (source) => String(source.id) === selectedSource,
      );
      const uploadOptions =
        uploadType === 'clicker'
          ? {
              ...(selectedAdvertiser ? { advertiserId: selectedAdvertiser } : {}),
              ...(selectedCampaign ? { campaignId: selectedCampaign } : {}),
              dataType: resolvedDataType,
            }
          : {
              ...(selectedSource
                ? {
                    source: selectedSourceRow?.name ?? selectedSource,
                    sourceId: selectedSource,
                  }
                : {}),
              dataType: resolvedDataType,
            };

      setStatusMessage('Uploading… 0%');
      setProgress(0);

      const uploadOutcome = await LeadsService.uploadLeads(selectedFile, {
        ...uploadOptions,
        onProgress: ({ percentage }) => {
          if (pollSessionRef.current !== session) return;
          const transferPct = Math.max(0, Math.min(100, Math.round(percentage)));
          setUploadPhase('uploading');
          setProgress(transferPct);
          if (percentage >= 100) {
            setStatusMessage('Finishing file upload… 100%');
            return;
          }
          setStatusMessage(`Uploading file… ${transferPct}%`);
        },
      });

      if (pollSessionRef.current !== session) return;

      const priorLastId = uploadOutcome.upload_async_prior_last_id ?? 0;
      let jobId = uploadOutcome.upload_async_job_id
        ? String(uploadOutcome.upload_async_job_id)
        : '';

      setProgress(100);
      setStatusMessage('File upload complete (100%).');
      await sleep(400);

      if (pollSessionRef.current !== session) return;

      setUploadPhase('processing');

      const applyJobProgress = (job: UploadJob, trustJob: boolean, polledAt: string) => {
        const totalR = safeInt(job.total_records, 0);
        const procR = safeInt(job.processed_records, 0);
        const insertedR = safeInt(job.inserted_records, 0);
        const dupR = safeInt(job.duplicate_records, 0);
        const pct = computeJobPercent(job);
        const pctLabel = formatProgressFromJob(job);

        setJobSnapshot({
          jobId: String(job.id),
          status: job.status || '',
          progressDisplay: pctLabel,
          processed: procR,
          total: totalR,
          filename: job.filename ?? '',
          inserted: insertedR,
          duplicates: dupR,
        });
        setProgress(trustJob ? Math.min(99, Math.max(0, pct)) : 0);
        setStatusMessage(
          `Job running · ${job.status} · ${pctLabel}% · ${procR.toLocaleString()} / ${totalR.toLocaleString()} rows · updated ${polledAt}`,
        );
      };

      const finishWithJob = (job: UploadJob) => {
        const mapped = jobToUploadResult(job);
        setTotalRecordsData([]);
        setValidRecordsData([]);
        setDuplicateErrorData([]);
        setUploadResult(mapped);
        setProgress(100);
        setStatusMessage('Upload completed successfully.');
        setUploadPhase('idle');
      };

      // Step 1: resolve job id via upload response or POST latest-job (short interval, not 30s cron)
      if (!jobId) {
        setProgress(0);
        setStatusMessage('Looking for your upload job (latest-job)…');
        const lookupStarted = Date.now();

        while (!jobId && Date.now() - lookupStarted < JOB_LOOKUP_MAX_MS) {
          if (pollSessionRef.current !== session) return;

          const polledAt = new Date().toLocaleTimeString();
          setLastPolledAt(polledAt);

          try {
            const snap: LatestUploadJobPayload = await LeadsService.getLatestUploadJob();
            const rawLastId = safeInt(snap.last_id, 0);
            const job = snap.job;

            if (job?.id) {
              const isNewByLastId = rawLastId > priorLastId;
              const active = isActiveJobStatus(job.status);
              if (isNewByLastId || active) {
                jobId = String(job.id);
                const trustJob = isNewByLastId || active;
                applyJobProgress(job, trustJob, polledAt);
                if (isJobComplete(job, trustJob)) {
                  finishWithJob(job);
                  return;
                }
                if (trustJob && isFailedJobStatus(job.status)) {
                  throw new Error(job.error_message || `Upload job ${job.status}`);
                }
                break;
              }
            }

            setStatusMessage(`Waiting for job to start… (${polledAt})`);
          } catch (err) {
            if (pollSessionRef.current !== session) return;
            setStatusMessage(
              err instanceof Error ? `${err.message} · Retrying…` : 'Could not reach latest-job · Retrying…',
            );
          }

          await sleep(POLL_LATEST_MS);
        }

        if (!jobId) {
          throw new Error('Timed out waiting for upload job to appear. Please try again.');
        }
      } else {
        setProgress(0);
        setStatusMessage(`Job #${jobId} queued. Checking status…`);
      }

      // Step 2: poll GET leads-upload/status/{id} until done
      const runStarted = Date.now();
      let statusPollCount = 0;

      while (Date.now() - runStarted < JOB_RUN_MAX_MS) {
        if (pollSessionRef.current !== session) return;

        statusPollCount += 1;
        const polledAt = new Date().toLocaleTimeString();
        setLastPolledAt(polledAt);

        let job: UploadJob;
        try {
          job = await LeadsService.getUploadJobStatus(jobId);
        } catch (err) {
          if (pollSessionRef.current !== session) return;
          setStatusMessage(
            err instanceof Error
              ? `${err.message} · Retrying status check…`
              : 'Status check failed · Retrying…',
          );
          await sleep(POLL_STATUS_MS);
          continue;
        }

        if (pollSessionRef.current !== session) return;

        const trustJob = true;
        applyJobProgress(job, trustJob, polledAt);

        if (isFailedJobStatus(job.status)) {
          throw new Error(job.error_message || `Upload job ${job.status}`);
        }

        if (isJobComplete(job, trustJob)) {
          finishWithJob(job);
          return;
        }

        if (!isActiveJobStatus(job.status) && statusPollCount > 3) {
          setStatusMessage(`Job status: ${job.status} · still checking…`);
        }

        await sleep(POLL_STATUS_MS);
      }

      throw new Error('Timed out waiting for upload job to finish.');
    } catch (error) {
      if (pollSessionRef.current === session) {
        setJobSnapshot(null);
        setStatusMessage(error instanceof Error ? error.message : 'Failed to upload. Please try again.');
        setUploadPhase('idle');
      }
    } finally {
      if (pollSessionRef.current === session) {
        clearJobPollTimer();
        setUploading(false);
      }
    }
  }; 

  const resetUpload = () => {
    pollSessionRef.current += 1;
    clearJobPollTimer();
    setSelectedFile(null);
    setPreviewData([]);
    setUploadResult(null);
    setSelectedAdvertiser('');
    setSelectedCampaign('');
    setSelectedSource('');
    setProgress(0);
    setUploadPhase('idle');
    setStatusMessage('');
    setUploading(false);
    setCsvHeaders([]);
    setUploadType('raw');
    setJobSnapshot(null);
    setLastPolledAt(null);
  };

  const handleDownloadDemoCsv = () => {
    const rawHeaders = [
      'name', 'phone', 'email', 'company_name', 'address', 'vehicle_brand_name',
      'dnd_status', 'plan', 'dob', 'age', 'income', 'city', 'pincode', 'source', 'data_type',
    ];
    const clickerHeaders = [
      'name', 'phone', 'email', 'company_name', 'address', 'vehicle_brand_name',
      'dnd_status', 'plan', 'dob', 'age', 'income', 'city', 'pincode', 'source',
    ];

    const demoRows =
      uploadType === 'clicker'
        ? [
            clickerHeaders,
            ['John Doe', '9876543210', 'john@example.com', 'Acme Inc', '123 Main St', 'Acme', 'No', 'Basic', '1990-01-01', '33', '50000', 'Delhi', '110001', 'Demo'],
            ['Jane Smith', '9123456789', 'jane@example.com', 'Globex Corp', '456 Oak Ave', 'Globex', 'Yes', 'Premium', '1985-05-10', '38', '70000', 'Mumbai', '400001', 'Demo'],
          ]
        : [
            rawHeaders,
            ['John Doe', '9876543210', 'john@example.com', 'Acme Inc', '123 Main St', 'Acme', 'No', 'Basic', '1990-01-01', '33', '50000', 'Delhi', '110001', 'Demo', 'lead'],
            ['Jane Smith', '9123456789', 'jane@example.com', 'Globex Corp', '456 Oak Ave', 'Globex', 'Yes', 'Premium', '1985-05-10', '38', '70000', 'Mumbai', '400001', 'Demo', 'lead'],
          ];

    const csv = demoRows.map((row) => row.map((val) => `"${val}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = uploadType === 'clicker' ? 'demo_clicker.csv' : 'demo_raw_data_leads.csv';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const getUploadValidationMessage = () => {
    if (!selectedFile) return 'Please select a file to upload.';
    return '';
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Upload Leads"
        subtitle="Bulk upload leads via CSV file"
        subtitleClassName="text-accent-100 text-sm"
      />

      <div className="bg-white rounded-lg border-gray-200 border lg:p-6 p-4">
        {/* Upload Section FIRST */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
        />
        <div
          role="button"
          tabIndex={0}
          className={`border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="text-center pointer-events-none">
            <Upload size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Drop your CSV file here, or click to browse
            </p>
            <p className="text-sm text-gray-600">Supports large CSV files (up to 10 GB).</p>
            {selectedFile && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-center">
                  <FileText size={20} className="text-primary-600 mr-2" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                  <span className="text-sm text-gray-500 ml-2">({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            type="button"
            className="px-4 py-2.5 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors text-sm font-medium"
            onClick={handleDownloadDemoCsv}
          >
            {uploadType === 'clicker'
              ? 'Download Clicker Demo CSV'
              : 'Download Raw Data Demo CSV'}
          </button>
        </div>
        
        <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50/60 p-5 sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-gray-900">Upload Configuration</h2>
            <p className="mt-1 text-sm text-gray-500">
              Choose upload type and fill the relevant fields. Only the CSV file is required to upload.
            </p>
          </div>

          <div
            className="mb-6 inline-flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-white p-1"
            role="tablist"
            aria-label="Upload type"
          >
            <button
              type="button"
              role="tab"
              aria-selected={uploadType === 'raw'}
              onClick={() => handleUploadTypeChange('raw')}
              className={cn(
                'rounded-md px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
                uploadType === 'raw'
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              )}
            >
              Raw Data Upload (Leads)
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={uploadType === 'clicker'}
              onClick={() => handleUploadTypeChange('clicker')}
              className={cn(
                'rounded-md px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
                uploadType === 'clicker'
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              )}
            >
              Clicker Data Upload
            </button>
          </div>

          {dataLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              <p className="mt-2 text-sm text-gray-600">
                {uploadType === 'clicker'
                  ? 'Loading advertisers and campaigns...'
                  : 'Loading sources...'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {uploadType === 'clicker' && (
                <>
                  <div>
                    <label htmlFor="upload-advertiser" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Advertiser <span className="font-normal text-gray-400"></span>
                    </label>
                    <div className="relative">
                      <select
                        id="upload-advertiser"
                        value={selectedAdvertiser}
                        onChange={(e) => setSelectedAdvertiser(e.target.value)}
                        className={selectFieldClass}
                        disabled={advertisers.length === 0}
                      >
                        <option value="">
                          {advertisers.length === 0 ? 'No advertisers available' : 'Select advertiser'}
                        </option>
                        {advertisers.map(advertiser => (
                          <option key={advertiser.id} value={advertiser.id}>{advertiser.name}</option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    {advertisers.length === 0 && (
                      <p className="text-xs text-red-500 mt-1.5">Failed to load advertisers. Please refresh the page.</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="upload-campaign" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Campaign <span className="font-normal text-gray-400"></span>
                    </label>
                    <div className="relative">
                      <select
                        id="upload-campaign"
                        value={selectedCampaign}
                        onChange={(e) => setSelectedCampaign(e.target.value)}
                        className={selectFieldClass}
                        disabled={campaignsLoading || (Boolean(selectedAdvertiser) && campaigns.length === 0)}
                      >
                        <option value="">
                          {!selectedAdvertiser
                            ? 'Select campaign'
                            : campaignsLoading
                              ? 'Loading campaigns...'
                              : campaigns.length === 0
                                ? 'No campaigns available'
                                : 'Select campaign'}
                        </option>
                        {campaigns.map(campaign => (
                          <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    {selectedAdvertiser && !campaignsLoading && campaigns.length === 0 && (
                      <p className="text-xs text-red-500 mt-1.5">No campaigns found for selected advertiser.</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="upload-clicker-data-type" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Data Type <span className="font-normal text-gray-400"></span>
                    </label>
                    <div className="relative">
                      <select
                        id="upload-clicker-data-type"
                        value="clicker"
                        disabled
                        className={selectFieldClass}
                        aria-label="Data type clicker"
                      >
                        <option value="clicker">Clicker</option>
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </>
              )}

              {uploadType === 'raw' && (
                <>
                  <div>
                    <label htmlFor="upload-source" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Source <span className="font-normal text-gray-400"></span>
                    </label>
                    <div className="relative">
                      <select
                        id="upload-source"
                        value={selectedSource}
                        onChange={(e) => setSelectedSource(e.target.value)}
                        className={selectFieldClass}
                        disabled={sources.length === 0}
                      >
                        <option value="">
                          {sources.length === 0 ? 'No sources available' : 'Select source'}
                        </option>
                        {sources.map(source => (
                          <option key={source.id} value={String(source.id)}>{source.name}</option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    {sources.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1.5">No sources loaded. Upload will proceed without a source.</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="upload-data-type" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Data Type <span className="font-normal text-gray-400"></span>
                    </label>
                    <div className="relative">
                      <select
                        id="upload-data-type"
                        value="lead"
                        disabled
                        className={selectFieldClass}
                        aria-label="Data type lead"
                      >
                        <option value="lead">Lead</option>
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {csvHeaders.includes('data_type') && (
            <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-600 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">Data Type Detected</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Your CSV already contains a <code className="bg-green-100 px-1 rounded">data_type</code> column.
                    Values from the file will be used automatically.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {uploading && (
            <div className="mt-6">
                <div className="flex justify-between mb-1">
                    <span className="text-base font-medium text-primary-700">{statusMessage}</span>
                    <span className="text-sm font-medium text-primary-700">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className={`h-2.5 rounded-full ${uploadPhase === 'processing' ? 'bg-primary-500 animate-pulse' : 'bg-primary-600'}`} style={{ width: `${progress}%` }}></div>
                </div>
                {uploadPhase === 'processing' && (
                  <div className="mt-2 space-y-2 text-sm text-primary-700">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                      <span>
                        File upload finished at 100%. Job progress updates every few seconds via status API.
                      </span>
                      {lastPolledAt && (
                        <p className="text-xs text-gray-600">Last status check: {lastPolledAt}</p>
                      )}
                    </div>
                    {jobSnapshot && (
                      <div className="rounded-md border border-primary-100 bg-primary-50/60 px-3 py-2 text-xs text-gray-800">
                        <p><span className="font-medium">Job</span> #{jobSnapshot.jobId} · <span className="font-medium">File</span> {jobSnapshot.filename ?? '—'}</p>
                        <p><span className="font-medium">API status</span> {jobSnapshot.status} · <span className="font-medium">Progress</span> {jobSnapshot.progressDisplay}%</p>
                        <p><span className="font-medium">Rows</span> {jobSnapshot.processed.toLocaleString()} / {jobSnapshot.total.toLocaleString()} · <span className="font-medium">Inserted</span> {jobSnapshot.inserted.toLocaleString()} · <span className="font-medium">Duplicates</span> {jobSnapshot.duplicates.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                )}
            </div>
        )}

        {!uploading && statusMessage && (
            <div className={`mt-4 text-center ${
              statusMessage.includes('Error') || statusMessage.includes('Failed') || statusMessage.includes('No')
                ? 'text-red-600' 
                : statusMessage.includes('successfully')
                ? 'text-green-600'
                : 'text-gray-600'
            }`}>
              {statusMessage}
            </div>
        )}

        {previewData.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Preview (First 5 Rows)</h3>
            <div className="overflow-x-auto bg-gray-50 rounded-lg">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    {Object.keys(previewData[0] || {}).map(header => (
                      <th key={header} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index} className="border-t border-gray-200">
                      {Object.values(row).map((value: unknown, i) => (
                        <td key={i} className="px-4 py-2 text-sm text-gray-900">{String(value)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-4 mt-6">
          <button onClick={resetUpload} className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">Reset</button>
          <button
            onClick={() => {
              const validationMessage = getUploadValidationMessage();
              if (validationMessage) {
                setStatusMessage(validationMessage);
                return;
              }
              handleUpload();
            }}
            disabled={uploading || dataLoading}
            className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 rounded-md transition-all transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : dataLoading ? 'Loading data...' : <><Upload size={16} className="inline mr-2" />Upload Leads</>}
          </button>
        </div>
      </div>
      
      {uploadResult && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Results</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg cursor-pointer hover:bg-blue-100" onClick={handleTotalRecordsClick} title="Download Total Records CSV">
              <div className="flex items-center">
                <FileText size={20} className="text-blue-600 mr-2" />
                <div>
                  <p className="text-sm text-blue-600">Total Records</p>
                  <p className="text-2xl font-bold text-blue-900">{uploadResult.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg cursor-pointer hover:bg-green-100" onClick={handleValidRecordsClick} title="Download Valid Records CSV">
              <div className="flex items-center">
                <CheckCircle size={20} className="text-green-600 mr-2" />
                <div>
                  <p className="text-sm text-green-600">Valid Records</p>
                  <p className="text-2xl font-bold text-green-900">{uploadResult.valid}</p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg cursor-pointer hover:bg-red-100" onClick={handleDuplicateErrorClick} title="Download Duplicates/Errors CSV">
              <div className="flex items-center">
                <XCircle size={20} className="text-red-600 mr-2" />
                <div>
                  <p className="text-sm text-red-600">Duplicates/Errors</p>
                  <p className="text-2xl font-bold text-red-900">{uploadResult.duplicates + uploadResult.errors.length}</p>
                </div>
              </div>
            </div>
          </div>
          {uploadResult.errors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <AlertCircle size={20} className="text-yellow-600 mr-2" />
                <h4 className="text-sm font-medium text-yellow-800">Validation Errors (Showing first 10)</h4>
              </div>
              <ul className="text-sm text-yellow-700 space-y-1 max-h-40 overflow-y-auto">
                {uploadResult.errors.slice(0, 10).map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadsUpload;
